/**
 * Analyzes patient events and returns a list of steps with status.
 * @param {Array} eventos - Patient events from API or mock
 * @param {Array} quiebres - Known quiebres/gaps (from mock data)
 * @returns {Array<{ id, titulo, descripcion, estado, fecha, diasDesde, severidad, accion, link, etapa, icono }>}
 *
 * estado: "completado" | "agendado" | "pendiente" | "urgente"
 */
export function detectarProximosPasos(eventos, quiebres = []) {
  const pasos = [];
  const now = new Date();

  // Group events by ambito
  const porAmbito = {};
  for (const e of eventos) {
    const a = e.ambito?.toLowerCase() || "";
    if (!porAmbito[a]) porAmbito[a] = [];
    porAmbito[a].push(e);
  }

  // Get most recent event dates by type
  const ultimaConsulta = getUltimaFecha(porAmbito, ["consulta", "ambulatorio"]);
  const ultimoLab = getUltimaFecha(porAmbito, ["laboratorio", "lab"]);
  const ultimaImagen = getUltimaFecha(porAmbito, ["imagen", "radiograf", "ecograf"]);
  const ultimoPago = getUltimaFecha(porAmbito, ["pago", "bono"]);
  const ultimaAgenda = getUltimaFecha(porAmbito, ["agenda"]);

  // 1. Check for completed recent steps (last 60 days)
  if (ultimaConsulta && diasDesde(ultimaConsulta) < 60) {
    pasos.push({
      id: "consulta-ok",
      titulo: "Consulta médica",
      descripcion: findDescripcion(porAmbito, ["consulta", "ambulatorio"]),
      estado: "completado",
      fecha: ultimaConsulta,
      diasDesde: diasDesde(ultimaConsulta),
      etapa: "Atención",
      icono: "Stethoscope",
    });
  }

  if (ultimoLab && diasDesde(ultimoLab) < 90) {
    pasos.push({
      id: "lab-ok",
      titulo: "Exámenes de laboratorio",
      descripcion: findDescripcion(porAmbito, ["laboratorio", "lab"]),
      estado: "completado",
      fecha: ultimoLab,
      diasDesde: diasDesde(ultimoLab),
      etapa: "Diagnóstica",
      icono: "FlaskConical",
    });
  }

  if (ultimaImagen && diasDesde(ultimaImagen) < 90) {
    pasos.push({
      id: "imagen-ok",
      titulo: "Exámenes de imagen",
      descripcion: findDescripcion(porAmbito, ["imagen", "radiograf", "ecograf"]),
      estado: "completado",
      fecha: ultimaImagen,
      diasDesde: diasDesde(ultimaImagen),
      etapa: "Diagnóstica",
      icono: "Scan",
    });
  }

  if (ultimoPago && diasDesde(ultimoPago) < 60) {
    pasos.push({
      id: "pago-ok",
      titulo: "Pago / Bono",
      descripcion: findDescripcion(porAmbito, ["pago", "bono"]),
      estado: "completado",
      fecha: ultimoPago,
      diasDesde: diasDesde(ultimoPago),
      etapa: "Financiero",
      icono: "CreditCard",
    });
  }

  // 2. Detect pending steps from quiebres
  for (const q of quiebres) {
    const dias = q.fecha ? diasDesde(q.fecha) : 999;
    pasos.push({
      id: `quiebre-${q.fecha}`,
      titulo: q.descripcion.split("—")[0]?.trim() || q.descripcion,
      descripcion: q.descripcion,
      estado: q.severidad === "alta" ? "urgente" : "pendiente",
      fecha: q.fecha,
      diasDesde: dias,
      severidad: q.severidad,
      accion: q.accion,
      link: "https://agenda.ucchristus.cl",
      etapa: detectarEtapaQuiebre(q.descripcion),
      icono: q.severidad === "alta" ? "AlertTriangle" : "Clock",
    });
  }

  // 3. Detect pending steps from event gaps
  // If last consulta > 90 days ago, suggest control
  if (ultimaConsulta && diasDesde(ultimaConsulta) > 90) {
    pasos.push({
      id: "control-pendiente",
      titulo: "Control médico pendiente",
      descripcion: `Última consulta hace ${diasDesde(ultimaConsulta)} días. Se recomienda agendar control.`,
      estado: diasDesde(ultimaConsulta) > 180 ? "urgente" : "pendiente",
      fecha: ultimaConsulta,
      diasDesde: diasDesde(ultimaConsulta),
      accion: "Agendar control médico",
      link: "https://agenda.ucchristus.cl",
      etapa: "Seguimiento",
      icono: "CalendarCheck",
    });
  }

  // If last lab > 180 days ago and patient has had labs before
  if (ultimoLab && diasDesde(ultimoLab) > 180) {
    pasos.push({
      id: "lab-control",
      titulo: "Exámenes de control pendientes",
      descripcion: `Último laboratorio hace ${diasDesde(ultimoLab)} días. Verificar si requiere control.`,
      estado: "pendiente",
      fecha: ultimoLab,
      diasDesde: diasDesde(ultimoLab),
      accion: "Consultar con médico tratante",
      link: "https://agenda.ucchristus.cl",
      etapa: "Diagnóstica",
      icono: "FlaskConical",
    });
  }

  // Sort: urgente first, then pendiente, then agendado, then completado
  const orden = { urgente: 0, pendiente: 1, agendado: 2, completado: 3 };
  pasos.sort((a, b) => (orden[a.estado] ?? 4) - (orden[b.estado] ?? 4));

  return pasos;
}

function getUltimaFecha(porAmbito, keywords) {
  for (const [ambito, eventos] of Object.entries(porAmbito)) {
    if (keywords.some((k) => ambito.includes(k))) {
      const sorted = eventos
        .filter((e) => e.fecha)
        .sort((a, b) => b.fecha.localeCompare(a.fecha));
      return sorted[0]?.fecha || null;
    }
  }
  return null;
}

function findDescripcion(porAmbito, keywords) {
  for (const [ambito, eventos] of Object.entries(porAmbito)) {
    if (keywords.some((k) => ambito.includes(k))) {
      const sorted = eventos
        .filter((e) => e.fecha)
        .sort((a, b) => b.fecha.localeCompare(a.fecha));
      return sorted[0]?.descripcion || "";
    }
  }
  return "";
}

function diasDesde(fechaStr) {
  if (!fechaStr) return 999;
  const fecha = new Date(fechaStr);
  const now = new Date();
  return Math.floor((now - fecha) / (1000 * 60 * 60 * 24));
}

function detectarEtapaQuiebre(desc) {
  const d = desc.toLowerCase();
  if (d.includes("lab") || d.includes("muestra")) return "Diagnóstica";
  if (d.includes("imagen") || d.includes("ecograf") || d.includes("radiograf"))
    return "Diagnóstica";
  if (d.includes("cirugía") || d.includes("pabellón")) return "Tratamiento";
  if (d.includes("control") || d.includes("seguimiento")) return "Seguimiento";
  if (d.includes("agenda") || d.includes("cita")) return "Acceso";
  return "Atención";
}

/**
 * Calculate progress percentage
 */
export function calcularProgreso(pasos) {
  if (pasos.length === 0) return { completados: 0, total: 0, pct: 0 };
  const completados = pasos.filter((p) => p.estado === "completado").length;
  return {
    completados,
    total: pasos.length,
    pct: Math.round((completados / pasos.length) * 100),
  };
}
